
import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { PersonalInfoFields } from "./forms/PersonalInfoFields";
import { PromoCodeSection } from "./forms/PromoCodeSection";
import { ConsentSection } from "./forms/ConsentSection";
import { CustomerFormData, customerFormSchema } from "./forms/types";

interface CustomerFormProps {
  onSubmit: (data: CustomerFormData & { customerProfileId?: string }) => void;
  eventId: string;
  subtotal: number;
  onPromoCodeApplied: (discountPercentage: number) => void;
  eventCurrency?: string;
  renderQRPhButton?: (form: any) => React.ReactNode;
}

export const CustomerForm = ({ 
  onSubmit, 
  eventId, 
  subtotal, 
  onPromoCodeApplied,
  eventCurrency,
  renderQRPhButton
}: CustomerFormProps) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const form = useForm<CustomerFormData>({
    resolver: zodResolver(customerFormSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phoneCountryCode: "+61",
      phoneNumber: "",
      marketingConsent: true,
      termsAccepted: false,
      promoCode: "",
    },
  });

  const handleSubmit = async (data: CustomerFormData) => {
    if (!data.termsAccepted) {
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);
    
    try {
      const normalizedEmail = data.email.toLowerCase().trim();
      console.log("Starting customer profile check with email:", normalizedEmail);
      
      // Check if customer profile exists
      const { data: existingProfile, error: profileError } = await supabase
        .from('customer_profiles')
        .select('id, email')
        .eq('email', normalizedEmail)
        .maybeSingle();

      if (profileError) {
        console.error("Error checking existing profile:", profileError);
        throw new Error(`Failed to check existing profile: ${profileError.message}`);
      }

      console.log("Profile check result:", existingProfile || "No existing profile found");

      let profileId;
      
      if (existingProfile) {
        console.log("Using existing profile:", existingProfile);
        profileId = existingProfile.id;
      } else {
        console.log("Creating new customer profile");
        // Create new profile
        const { data: newProfile, error: createError } = await supabase
          .from('customer_profiles')
          .insert({
            email: normalizedEmail,
            first_name: data.firstName,
            last_name: data.lastName,
            phone_number: data.phoneNumber,
            phone_country_code: data.phoneCountryCode,
            marketing_opt_in: data.marketingConsent,
            privacy_accepted: data.termsAccepted
          })
          .select()
          .single();

        if (createError) {
          console.error("Error creating profile:", createError);
          throw new Error(`Failed to create profile: ${createError.message}`);
        }
        
        console.log("New profile created successfully:", newProfile);
        profileId = newProfile.id;
      }

      console.log("Customer profile processed successfully with ID:", profileId);

      // Pass the data with the customer profile ID and individual user fields
      onSubmit({
        ...data,
        customerProfileId: profileId
      });
    } catch (error) {
      console.error("Payment processing error:", error);
      const errorMsg = error instanceof Error ? error.message : "Failed to process payment. Please try again.";
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <PersonalInfoFields form={form} eventCurrency={eventCurrency} />
        
        <PromoCodeSection 
          form={form} 
          eventId={eventId} 
          onPromoCodeApplied={onPromoCodeApplied} 
        />
        
        <ConsentSection form={form} />

        <div className="space-y-4">
          <Button
            type="submit"
            disabled={isProcessing}
            className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
          >
            {isProcessing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              "Pay with Credit Card"
            )}
          </Button>
          
          {renderQRPhButton && renderQRPhButton(form) && (
            <>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-gray-600" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-gray-800 px-2 text-gray-400">
                    Or pay with
                  </span>
                </div>
              </div>
              {renderQRPhButton(form)}
            </>
          )}
        </div>
        
        {errorMessage && (
          <div className="mt-2 text-sm font-medium" style={{ color: "#dc143c" }}>
            {errorMessage}
          </div>
        )}
      </form>
    </Form>
  );
};
